// Top-level lanes used to group care blocks in the dashboard UI.
export const BLOCK_TYPE = {
  MindGut: 'mind-gut',
  BehaviorChange: 'behavior change',
  Clinical: 'clinical',
  Nutrition: 'nutrition',
  Visit: 'visit',
} as const;

export type BlockType = typeof BLOCK_TYPE[keyof typeof BLOCK_TYPE];

// Known archetypes for dashboard block templates.
export enum BlockArchetype {
  Goal = 'goal',
  Program = 'program',
  Task = 'task',
  Contraindicated = 'contraindicated',
}

export const BLOCK_STABLE_STRINGS = {
  identifySuspects: 'identify_suspects',
  eliminationChallenge: 'elimination_challenge',
  foodChallenge: 'food_challenge',
  generalLogging: 'general_logging',
  mindGutMasterclass: 'mind_gut_masterclass',
  dailyGutReset: 'daily_gut_reset',
  nutritionContraindicated: 'nutrition_contraindicated',
  mindGutContraindicated: 'mind-gut_contraindicated',
  uncoveredGoals: 'uncovered_goals',
} as const;

export type BlockStableStrings =
  typeof BLOCK_STABLE_STRINGS[keyof typeof BLOCK_STABLE_STRINGS];

export type AdjustableNutritionBlockString =
  | typeof BLOCK_STABLE_STRINGS.identifySuspects
  | typeof BLOCK_STABLE_STRINGS.eliminationChallenge
  | typeof BLOCK_STABLE_STRINGS.foodChallenge;

export const ADJUSTABLE_NUTRITION_BLOCKS: string[] = [
  BLOCK_STABLE_STRINGS.identifySuspects,
  BLOCK_STABLE_STRINGS.eliminationChallenge,
  BLOCK_STABLE_STRINGS.foodChallenge,
];

// some blocks have instructions dynamically generated or hardcoded. No
// reason to edit their instructions as the changes won't be reflected
export const INSTRUCTIONS_NOT_EDITABLE_BLOCKS: string[] = [
  BLOCK_STABLE_STRINGS.foodChallenge,
  BLOCK_STABLE_STRINGS.generalLogging,
  BLOCK_STABLE_STRINGS.eliminationChallenge,
  BLOCK_STABLE_STRINGS.identifySuspects,
  BLOCK_STABLE_STRINGS.uncoveredGoals,
];

// Coach/provider identity embedded in `UserBlock.assigned_by`.
export type UserBlockAssignee = {
  id: number;
  full_name: string;
  display_name: string;
  healthie_id: number;
  image_url: string;
  type: string;
  care_member_type: string | null;
  uuid: string;
};

// User-specific assigned block instance returned by `/private/user/blocks/`.
// This is the block as attached to a member (dates, assignee, lifecycle).
export type UserBlock = {
  id: string;
  assigned_by: UserBlockAssignee | null;
  block_name: string;
  block_type: BlockType;
  date_assigned: string | null;
  date_ended: string | null;
  date_archived: string | null;
  instructions: string | null;
  archetype: BlockArchetype | null;
  metadata: {
    /** Count of gut checks logged by the patient against this block. */
    logged_gut_checks?: number | null;
    /**
     * Total gut checks prescribed for this block (e.g. 21 for a 3-week
     * Elimination Challenge). Drives EngagementAddon of-total mode.
     */
    gut_checks_total?: number | null;
    /** Date (YYYY-MM-DD, patient's timezone) the patient last engaged. */
    last_engaged?: string | null;
    /** Whether the Nudge banner should show for this block. */
    should_nudge?: boolean | null;
  };
  stable_string: string | null;
};

// Dashboard block catalog item returned by `/private/dashboard/blocks/`.
// This is the assignable block definition (template), not user-assigned state.
export type DashboardBlock = {
  id: string | null;
  name: string | null;
  block_type: BlockType;
  archetype: BlockArchetype | null;
  stable_string: string | null;
};
